import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, UserX } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ReportTradesmanAccountDialogProps {
  jobId: string;
  tradesmanId: string;
  tradesmanName: string;
  isOpen: boolean;
  onClose: () => void;
}

const TRADESMAN_REPORT_REASONS = [
  { value: "fake_profile", label: "Fake Profile or Identity" },
  { value: "no_insurance", label: "No Valid Insurance" },
  { value: "poor_workmanship", label: "Poor Quality Work" },
  { value: "overcharging", label: "Overcharging or Hidden Fees" },
  { value: "no_show", label: "Repeatedly Not Showing Up" },
  { value: "unprofessional_behavior", label: "Unprofessional Behavior" },
  { value: "safety_violations", label: "Safety Violations" },
  { value: "unlicensed_work", label: "Unlicensed or Unqualified Work" },
  { value: "property_damage", label: "Property Damage" },
  { value: "contract_violation", label: "Contract Violation" },
  { value: "inappropriate_conduct", label: "Inappropriate Conduct" },
  { value: "other", label: "Other" }
];

export const ReportTradesmanAccountDialog = ({ 
  jobId, 
  tradesmanId, 
  tradesmanName, 
  isOpen, 
  onClose 
}: ReportTradesmanAccountDialogProps) => {
  const [reportReason, setReportReason] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!reportReason) {
      toast({
        title: "Please select a reason",
        description: "You must select a reason for reporting this tradesman account.",
        variant: "destructive",
      });
      return;
    }

    if (!tradesmanId) {
      toast({
        title: "Missing tradesman information",
        description: "Unable to identify the tradesman to report. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Submitting tradesman account report:", {
        tradesmanId,
        jobId,
        reportReason,
        description
      });

      // Submit user report via edge function
      const { error } = await supabase.functions.invoke('submit-report', {
        body: {
          reported_user_id: tradesmanId,
          job_id: jobId,
          report_type: 'tradesman_account',
          reason: reportReason,
          description: description || null
        }
      });

      if (error) throw error;

      toast({
        title: "Account reported successfully",
        description: "Thank you for reporting this tradesman account. Our administrators will investigate and take appropriate action.",
      });

      // Reset form and close
      setReportReason("");
      setDescription("");
      onClose();
    } catch (error: any) {
      console.error("Error submitting account report:", error);
      toast({
        title: "Failed to submit report",
        description: error.message || "An error occurred while submitting your report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md z-[60]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-red-500" />
            Report Tradesman Account
          </DialogTitle>
          <DialogDescription>
            Report <strong>{tradesmanName}</strong>'s account for violations of platform policies or professional standards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Important</p>
                <p>This will report the tradesman's account and profile. Please ensure your report is accurate as false reports may result in restrictions on your account.</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for reporting this account *</Label>
            <Select value={reportReason} onValueChange={setReportReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent className="z-[70]">
                {TRADESMAN_REPORT_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Additional details (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide specific details about the issue with this tradesman's account or services..."
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/1000 characters
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-sm text-blue-800">
              <p className="font-medium">What happens next?</p>
              <ul className="mt-2 space-y-1 text-sm">
                <li>• Our team will investigate the tradesman's account</li>
                <li>• We may take action including warnings, suspension, or removal</li>
                <li>• You'll be notified of the investigation outcome</li>
                <li>• All reports are confidential and anonymous</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="flex-1">
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
              ) : null}
              Report Account
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};